import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Link2, Plus } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { ClientAgencyRelationship, Organization } from '../../types/domain';

export function RelationshipsPage() {
  const { accessToken, isPlatformAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [agency, setAgency] = useState('');
  const [client, setClient] = useState('');
  const organizations = useApiCollection<Organization>(
    'relationship-organizations',
    '/organizations/?page_size=100&ordering=name',
    accessToken,
    isPlatformAdmin,
  );
  const relationships = useApiCollection<ClientAgencyRelationship>(
    'organization-relationships',
    '/organization-relationships/?page_size=100&ordering=agency__name',
    accessToken,
    isPlatformAdmin,
  );
  const agencies = organizations.data?.results.filter((item) => item.type === 'agency') ?? [];
  const clients = organizations.data?.results.filter((item) => item.type === 'client') ?? [];
  const rows = relationships.data?.results ?? [];

  const createRelationship = useMutation({
    mutationFn: () => apiRequest<ClientAgencyRelationship>('/organization-relationships/', {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({ agency, client }),
    }),
    onSuccess: async () => {
      setAgency('');
      setClient('');
      await queryClient.invalidateQueries({ queryKey: ['collection', 'organization-relationships'] });
    },
  });

  const updateRelationship = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) => (
      apiRequest<ClientAgencyRelationship>(`/organization-relationships/${id}/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'PATCH',
        body: JSON.stringify({ is_active }),
      })
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['collection', 'organization-relationships'] });
    },
  });

  if (!isPlatformAdmin) return <Navigate replace to="/organisations" />;

  function submit(event: FormEvent) {
    event.preventDefault();
    createRelationship.mutate();
  }

  return (
    <>
      <Link className="back-link" to="/organisations"><ArrowLeft size={15} /> Back to organisations</Link>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Platform administration</p>
          <h1>Agency-client links</h1>
          <p>Authorize an agency to operate campaigns for a client organisation.</p>
        </div>
        <span className="record-count"><Link2 size={16} /> {relationships.data?.count ?? 0}</span>
      </header>

      <section className="content-card management-panel">
        <div className="card-heading">
          <div><h2>Create relationship</h2><p>Both organisations must already exist in Brand Field Crew.</p></div>
        </div>
        <form className="inline-management-form relationship-form" onSubmit={submit}>
          <label>
            Agency
            <select required value={agency} onChange={(event) => setAgency(event.target.value)}>
              <option value="">Select agency</option>
              {agencies.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>
            Client
            <select required value={client} onChange={(event) => setClient(event.target.value)}>
              <option value="">Select client</option>
              {clients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <button className="button primary" disabled={createRelationship.isPending} type="submit">{createRelationship.isPending ? <InlineSpinner /> : <Plus size={16} />} {createRelationship.isPending ? 'Linking…' : 'Link organisations'}</button>
        </form>
        {createRelationship.isError ? <p className="form-error" role="alert">{getApiErrorMessage(createRelationship.error)}</p> : null}
      </section>

      <DataPanel loading={relationships.isLoading || organizations.isLoading} error={relationships.isError || organizations.isError} empty={rows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Agency</th><th>Client</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {rows.map((relationship) => (
                  <tr key={relationship.id}>
                    <td><strong>{relationship.agency_name}</strong></td>
                    <td><strong>{relationship.client_name}</strong></td>
                    <td><StatusPill value={relationship.is_active ? 'active' : 'inactive'} /></td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="text-button"
                          disabled={updateRelationship.isPending}
                          onClick={() => updateRelationship.mutate({ id: relationship.id, is_active: !relationship.is_active })}
                          type="button"
                        >
                          {relationship.is_active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </DataPanel>
    </>
  );
}
